import {noop} from '@benchling/js-utils';
import React from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import BulkAddRowsToTransferTableFlow from 'bulk-import/BulkAddRowsToTransferTableFlow';
import DataConnector from 'connect/DataConnector';
import {ELN_ANALYTICS_ACTIONS, LEGACY_WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as SampleConstants from 'constants/SampleConstants';
import * as FileConstants from 'folder-items/FileConstants';
// @ts-expect-error
// eslint-disable-next-line unused-imports/no-unused-imports
import PrintLabelButton from 'label-printing/PrintLabelButton';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {createCollapseId} from 'notebook/mediocre/base-plugin/createCollapseId';
import {forwardSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import * as NoteEditorTableUtil from 'notebook/notes/plugins/plain-table/NoteEditorTableUtil';
import {ColumnDefinition, getColumnLabel} from 'notebook/structured-tables/columns/ColumnDefinitions';
import toInsertableColumnInfos from 'notebook/structured-tables/columns/toInsertableColumnInfos';
import PrintBarcodesButton from 'notebook/structured-tables/components/PrintBarcodesButton';
import RefreshStructuredTableButton from 'notebook/structured-tables/components/RefreshStructuredTableButton';
import TableHeaderPipeDivider from 'notebook/structured-tables/components/TableHeaderPipeDivider';
import {
  SchemaFieldColumnUpdate,
  StructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import hasBlockingValidationErrors from 'notebook/structured-tables/util/hasBlockingValidationErrors';
import {getSelectorIdsFromFields} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {Cells} from 'notebook/tables/constants/RowConstants';
import {ColumnConstraint, TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {Table, TableHeaderControlsArgs} from 'notebook/tables/constants/TableConstants';
import TableEditable from 'notebook/tables/core-ui/TableEditable';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {TableOpOutput} from 'notebook/tables/excess/ops/TableOps';
import {SimpleDownloadAsCSVButton} from 'notebook/tables/export/DownloadAsCSVButton';
import makeTableCSV from 'notebook/tables/export/makeTableCSV';
import {
  handleRefreshTableColumns,
  mergeColumnUpdatesIntoColumnConstraints,
} from 'notebook/tables/refresh-table-data/LegacyRefreshTableData';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {fillNextAvailablePositions} from 'notebook/transfer-tables/FillNextAvailablePositionsUtil';
import TransferTableStageRunSampleLinkContext from 'notebook/transfer-tables/link-contexts/TransferTableStageRunSampleLinkContext';
import TransferTableStandaloneLinkContext from 'notebook/transfer-tables/link-contexts/TransferTableStandaloneLinkContext';
import * as TransferTableActions from 'notebook/transfer-tables/TransferTableActions';
import {
  getSourceSampleContainerStructuredTableColumns,
  getSourceSampleType,
  getSupportedSampleTypesFromColumns,
  SourceSampleType,
} from 'notebook/transfer-tables/TransferTableColumnDefinitions';
import {
  TransferOperationParams,
  TransferTableAddMenuItem,
  TransferTableColumnGroup,
  TransferTableProps,
  TransferTableRow,
} from 'notebook/transfer-tables/TransferTableConstants';
import {
  getConcentrationForRow,
  getContainerBarcodesForRow,
  getContainerSchemaTagsDictForRow,
  getNumberOfContainersForRow,
  getParentStorableForRow,
  getPositionsForRow,
  getSourceBatchIdForRow,
  getSourceBioentityIdForRow,
  getSourceContainerIdForRow,
  getSourcePlateIdForRow,
  getSourcePositionForRow,
  getStageRunIdForRow,
  getVolumeForRow,
} from 'notebook/transfer-tables/TransferTableEditableRowsUtil';
import * as TransferTableNotePartUtil from 'notebook/transfer-tables/TransferTableNotePartUtil';
import * as TransferTableUtil from 'notebook/transfer-tables/TransferTableUtil';
import SchemaFieldSelectorStore from 'registry/schema-field-selector/SchemaFieldSelectorStore';
import CreateRequestFromSamplesButton from 'requests/creation/CreateRequestFromSamplesButton';
import {getAvailablePositionsByGridId, OrderingStrategy} from 'samples/grids/GridPositionUtil';
import {makeSampleDetailsFromSamples} from 'samples/StageRunSampleModelManager';
import * as StorableActions from 'samples/StorableActions';
import {nullableRESTStorableSummaryToGraphQL} from 'samples/storables/graphql/translateStorable';
import StorableStore from 'samples/StorableStore';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import * as RichObjectTable from 'ui-library/RichObjectTable';
import APIUtil from 'util/APIUtil';
import memoizeLastCall from 'util/memoizeLastCall';
import preserveShallowEquality from 'util/preserveShallowEquality';
import {withLoadingScreen} from 'util/Promises';
import {initiateTextDownload} from 'util/TextToBlobUtil';
import {WorkflowSelector} from 'workflows/reduceWorkflows';
import {StageRun} from 'workflows/WorkflowConstants';

interface ConnectedProps {
  gridsById: {[id: string]: SampleConstants.Grid | null};
  stageRuns: Array<StageRun>;
  selectorsById: {[selectorId: string]: SchemaFieldSelector};
  fileSummariesById: {[id: string]: FileConstants.BioentitySummary};
  batchesById: {[batchId: string]: SampleConstants.Batch};
}

function collectFinalizedBatchIds(rows: Array<TransferTableRow>, sampleColumnIndex: number): Array<string> {
  const batchIds = [];
  for (const row of rows) {
    const typedValue = row.cells[sampleColumnIndex].typedValue;
    if (
      row.isFinalized &&
      typedValue != null &&
      typedValue.type === BaseTypedValueType.BATCH &&
      typedValue?.batchInfo.id != null // user may not have permissions
    ) {
      batchIds.push(typedValue.batchInfo.id);
    }
  }
  return batchIds;
}

class TransferTableConnector extends DataConnector<TransferTableProps, ConnectedProps> {
  override stores = [SchemaFieldSelectorStore, StorableStore];

  enhanceProps: () => ConnectedProps;

  constructor(props: TransferTableProps) {
    super(props);
    this.enhanceProps = preserveShallowEquality(this.uncachedEnhanceProps);
  }

  uncachedEnhanceProps = (): ConnectedProps => {
    const sampleColumnIndex = TransferTableUtil.getTransferTableColumnDefinitionIndex(
      this.props.itemProps.table,
      TableColumnType.SAMPLE,
      TransferTableColumnGroup.SOURCE,
      false
    );
    const batchesById = this.getOrFetchMultipleOptionalDeprecated(
      ManagedModelType.BATCH,
      collectFinalizedBatchIds(this.props.rows, sampleColumnIndex)
    );

    return {
      gridsById: this.storableStore().getGrids(),
      stageRuns: new WorkflowSelector(this.reduxState().models.workflows).getOrderedStageRunsForEntry(
        this.props.entryId
      ),
      selectorsById: this.getOrFetchMultipleRequired(
        ManagedModelType.SCHEMA_FIELD_SELECTOR_BY_ID,
        getSelectorIdsFromFields(this.props.containerSchema.fields)
      ),
      fileSummariesById: this.reduxState().models.fileSummariesById,
      batchesById,
    };
  };
}

interface ButtonStatus {
  tooltipTitle: string;
  disabled: boolean;
}

/**
 * Inner component that assumes all data is loaded and is responsible for rendering the actual table.
 *
 * This is a legacy transfer table, not a StructuredTable.
 *
 * @deprecated This table is no longer supported, please do not further proliferate its use.
 */
export class TransferTable extends React.PureComponent<TransferTableProps & ConnectedProps> {
  static selectorsLink = {
    // selectors are not enabled, but TableEditable still requires this
    value: [],
    selectorUsageCounts: {},
  };

  getRequestableSamples = memoizeLastCall(
    (
      table: Table,
      rows: Array<TransferTableRow>,
      fileSummariesById: {[id: string]: FileConstants.BioentitySummary},
      batchesById: {[batchId: string]: SampleConstants.Batch}
    ) => TransferTableUtil.getTransferredSamples(table, rows, fileSummariesById, batchesById)
  );

  _getTransferredRows(): Array<TransferTableRow> {
    return this.props.rows.filter((row) => row.isFinalized);
  }

  _getEditableRows(): Array<TransferTableRow> {
    return this.props.rows.filter((row) => !row.isFinalized);
  }

  _isPlaceholderTable(): boolean {
    return this.props.rows.length === 0;
  }

  _structuredTableColumnToColumnDefinition = (
    structuredTableColumn: StructuredTableColumn
  ): ColumnDefinition =>
    TransferTableUtil.structuredTableColumnToColumnDefinition(
      structuredTableColumn,
      this.props.containerSchema,
      this.props.parentStorableIsGrid,
      this.props.stageRuns,
      TransferTableNotePartUtil.canChangeUnits(this.props.rows)
    );

  _getColumnDefinitions(): Array<ColumnDefinition> {
    return this.props.structuredTableColumns.map((column) =>
      this._structuredTableColumnToColumnDefinition(column)
    );
  }

  _getTransferTableForConvertToTable(): TransferTableNotePartUtil.TransferTableForConvertToTable {
    return {
      name: this.props.tableName,
      containerSchema: this.props.containerSchema,
      parentStorableIsGrid: this.props.parentStorableIsGrid,
      columns: this.props.structuredTableColumns,
      rows: this.props.rows,
    };
  }

  // Needs to be a NoteEditor-level table op in order to shift formulas from other tables
  // that reference this table
  _adjustSourceColumnsForNewSamplesTableOp(
    table: Table,
    sampleDetails: Array<SampleConstants.SampleDetails>
  ): TableOpOutput {
    const supportedSampleTypes: Set<SourceSampleType> = getSupportedSampleTypesFromColumns(
      this.props.structuredTableColumns
    );
    const addedSampleTypes = new Set(sampleDetails.map((details) => getSourceSampleType(details)));
    if (
      [...addedSampleTypes].every((sampleType) => supportedSampleTypes.has(sampleType)) &&
      [...supportedSampleTypes].every((sampleType) => addedSampleTypes.has(sampleType))
    ) {
      // Easy case - all sample types are compatible and also match the columns
      return {table, tokenUpdaters: [], box: null, dim: null};
    }
    // If there are no rows, we can remove any sample columns that aren't needed for the new rows.
    // Otherwise, we can't remove any columns, because we're not sure what columns the existing rows need.
    let newSampleTypes: Set<SourceSampleType>;
    if (this.props.rows.length === 0) {
      newSampleTypes = addedSampleTypes;
    } else {
      newSampleTypes = new Set([...supportedSampleTypes, ...addedSampleTypes]);
    }

    // Add or remove source sample columns to match the sample type
    const columnsToAdd = getSourceSampleContainerStructuredTableColumns(newSampleTypes);
    const columnInfosToAdd = columnsToAdd.map((structuredTableColumn) => {
      const columnDefinition = this._structuredTableColumnToColumnDefinition(structuredTableColumn);
      return {
        columnConstraints: {
          editLabelDisabled: true,
          columnDefinition,
          structuredTableColumn,
        },
        columnLabels: getColumnLabel(columnDefinition),
        widths: structuredTableColumn.width,
      };
    });
    const tableOpOutput = TransferTableUtil.replaceSourceColumns(table, columnInfosToAdd);
    tableOpOutput.table = TransferTableNotePartUtil.convertStructuredTableColumnsForTransferTable({
      table: tableOpOutput.table,
      useCellValueAsDefaultValue: false,
    });
    return tableOpOutput;
  }

  _handleUpdateTransferTable({rows}: {rows: Array<TransferTableRow>}): void {
    this.props.updateHandler(
      this.props.index,
      {
        table: TransferTableNotePartUtil.convertTransferTableToTable(
          {
            ...this._getTransferTableForConvertToTable(),
            rows,
          },
          this.props.stageRuns
        ),
      },
      undefined,
      {[this.props.tableName]: {type: 'eval-table'}}
    );
  }

  _handleAddSamples = (samplesWithDetails: Array<SampleConstants.SampleDetails>): void => {
    this.props.editorProps.performTableOp({
      tableName: this.props.tableName,
      op: (table) => {
        assert(table.name === this.props.tableName, 'targetTableName should match transfer table name');

        const tableOpOutput = this._adjustSourceColumnsForNewSamplesTableOp(table, samplesWithDetails);
        const newEditableRowCells: Cells = TransferTableUtil.getEditableRowsForSamples(
          tableOpOutput.table.columnConstraints as Array<ColumnConstraint>,
          samplesWithDetails,
          this.props.rows.length
        );
        tableOpOutput.table = TransferTableNotePartUtil.addEditableRowsToTable(
          tableOpOutput.table,
          newEditableRowCells
        );
        if (tableOpOutput.tableOp == null) {
          tableOpOutput.tableOp = {[tableOpOutput.table.name]: {type: 'eval-table'}};
        } else {
          tableOpOutput.tableOp[tableOpOutput.table.name] = {type: 'eval-table'};
        }
        return tableOpOutput;
      },
      options: {ignoreLockedDimensions: true},
    });
  };

  _handleImportNewRows = (importedRows: Array<TransferTableUtil.ImportedTransferTableRow>): void => {
    const sampleDetails = importedRows.map(({samplesForSampleDetails}) =>
      makeSampleDetailsFromSamples({
        ...samplesForSampleDetails,
        plate: nullableRESTStorableSummaryToGraphQL(samplesForSampleDetails.plate),
      })
    );
    this.props.editorProps.performTableOp({
      tableName: this.props.tableName,
      op: (originalTable) => {
        assert(
          originalTable.name === this.props.tableName,
          'targetTableName should match transfer table name'
        );

        const tableOpOutput = this._adjustSourceColumnsForNewSamplesTableOp(originalTable, sampleDetails);
        const columnDefinitions = tableOpOutput.table.columnConstraints.map(
          (columnConstraint) => columnConstraint!.columnDefinition!
        );
        const newEditableRowCells = importedRows.map((importedRow) =>
          TransferTableUtil.parseImportedRow(columnDefinitions, importedRow)
        );
        tableOpOutput.table = TransferTableNotePartUtil.addEditableRowsToTable(
          tableOpOutput.table,
          newEditableRowCells
        );
        if (tableOpOutput.tableOp == null) {
          tableOpOutput.tableOp = {[tableOpOutput.table.name]: {type: 'eval-table'}};
        } else {
          tableOpOutput.tableOp[tableOpOutput.table.name] = {type: 'eval-table'};
        }
        return tableOpOutput;
      },
      options: {ignoreLockedDimensions: true},
    });
  };

  _handleInsertFromSpreadsheet = (): void => {
    BulkAddRowsToTransferTableFlow.open({
      containerSchemaId: this.props.containerSchema.id,
      onImportRows: this._handleImportNewRows,
      fileId: this.props.editorProps.fileId,
      columnDefinitions: this._getColumnDefinitions(),
    });
  };

  _getDefaultStructuredTableColumns(): Array<StructuredTableColumn> {
    return TransferTableUtil.getDefaultStructuredTableColumns({
      containerSchema: this.props.containerSchema,
      parentStorableIsGrid: this.props.parentStorableIsGrid,
      selectorsById: this.props.selectorsById,
      fileId: this.props.editorProps.fileId,
    });
  }

  _getOperations(): Array<TransferOperationParams> {
    const operations = [];
    const columnDefinitions = this._getColumnDefinitions();
    const evaluatedEditableRows = this._getEvaluatedEditableRows();
    for (const evaluatedEditableRow of evaluatedEditableRows) {
      const sourceBioentityId = getSourceBioentityIdForRow(evaluatedEditableRow, columnDefinitions);
      const sourceBatchId = getSourceBatchIdForRow(evaluatedEditableRow, columnDefinitions);
      const sourceContainerId = getSourceContainerIdForRow(evaluatedEditableRow, columnDefinitions);
      const sourcePlateId = getSourcePlateIdForRow(evaluatedEditableRow, columnDefinitions);
      const sourcePosition = getSourcePositionForRow(evaluatedEditableRow, columnDefinitions);
      const stageRunId = getStageRunIdForRow(evaluatedEditableRow, columnDefinitions);
      const parentStorableSummary = getParentStorableForRow(evaluatedEditableRow, columnDefinitions);
      const positions = this.props.parentStorableIsGrid
        ? getPositionsForRow(evaluatedEditableRow, columnDefinitions)
        : null;
      const numberOfContainers = getNumberOfContainersForRow(evaluatedEditableRow, columnDefinitions);
      const containerBarcodes = getContainerBarcodesForRow(evaluatedEditableRow, columnDefinitions);
      const volume = getVolumeForRow(evaluatedEditableRow, columnDefinitions);
      const concentration = getConcentrationForRow(evaluatedEditableRow, columnDefinitions);
      const containerSchemaTagsDict = getContainerSchemaTagsDictForRow(
        evaluatedEditableRow,
        columnDefinitions
      );
      operations.push({
        sourceBioentityId,
        sourceBatchId,
        sourceContainerId,
        sourcePlateId,
        sourcePosition,
        stageRunId,
        parentStorableData: {
          parentStorableId: parentStorableSummary.id,
          positions,
        },
        numberOfContainers,
        containerBarcodes,
        volume,
        concentration,
        containerSchemaTagsDict,
      });
    }
    return operations;
  }

  _handleTransfer = async (): Promise<void> => {
    // ensure any grids required for transfer are fetched and up-to-date
    const gridIds = this._getParentStorableIds(this._getEvaluatedEditableRows());
    if (gridIds.length > 0) {
      await withLoadingScreen(StorableActions.bulkFetchStorables(gridIds), 'Verifying transfer...');
    }

    const operations = this._getOperations();

    await TransferTableActions.transferSamples({
      entryId: this.props.entryId,
      transferTableProvenanceUuid: this.props.provenanceUuid,
      containerSchemaId: this.props.containerSchema.id,
      operations,
    });
  };

  _handleDownloadAsCSV = async (): Promise<void> => {
    const tableName = this.props.containerSchema.name;
    const cells = this.props.itemProps.table.table;
    const headerRow = this._getColumnDefinitions().map((columnDefinition) =>
      getColumnLabel(columnDefinition)
    );
    initiateTextDownload(
      await makeTableCSV({table: cells, computedTable: this.props.itemProps.computedTable, headerRow}),
      `${tableName}.csv`
    );
    const entryType = APIUtil.apiIdToType(this.props.editorProps.fileId);
    if (entryType === ModelTypeConstants.STAGE_ENTRY) {
      analytics.deprecatedTrackEvent(LEGACY_WORKFLOWS_ANALYTICS_ACTIONS.DOWNLOADED_TRANSFER_TABLE_AS_CSV);
    } else {
      analytics.deprecatedTrackEvent(ELN_ANALYTICS_ACTIONS.DOWNLOADED_TRANSFER_TABLE_IN_NOTEBOOK_AS_CSV);
    }
  };

  _getParentStorableIds(evaluatedEditableRows: Array<Array<EvaluatedCell>>): Array<string> {
    return _.chain(evaluatedEditableRows)
      .map((evaluatedEditableRow) =>
        getParentStorableForRow(evaluatedEditableRow, this._getColumnDefinitions(), {isRequired: false})
      )
      .compact()
      .pluck('id')
      .uniq()
      .value();
  }

  _handleFillNextAvailablePositions = async (orderingStrategy: OrderingStrategy): Promise<void> => {
    const gridIds = this._getParentStorableIds(this._getEvaluatedEditableRows());
    // fetch all grids to get latest filled positions
    if (gridIds.length > 0) {
      await withLoadingScreen(
        StorableActions.bulkFetchStorables(gridIds),
        'Calculating next available positions...'
      );
    }
    const availablePositionsByGridId = getAvailablePositionsByGridId({
      gridIds,
      gridsById: this.props.gridsById,
      containerSchemaId: this.props.containerSchema.id,
      orderingStrategy,
    });
    const newEditableRows = fillNextAvailablePositions({
      editableRows: this._getEditableRows(),
      evaluatedEditableRows: this._getEvaluatedEditableRows(),
      gridsById: this.props.gridsById,
      availablePositionsByGridId,
      columnDefinitions: this._getColumnDefinitions(),
    });
    this._handleUpdateTransferTable({rows: [...this._getTransferredRows(), ...newEditableRows]});
    const entryType = APIUtil.apiIdToType(this.props.editorProps.fileId);
    if (entryType === ModelTypeConstants.STAGE_ENTRY) {
      analytics.deprecatedTrackEvent(
        LEGACY_WORKFLOWS_ANALYTICS_ACTIONS.FILLED_NEXT_AVAILABLE_POSITIONS_FROM_TRANSFER_TABLE
      );
    } else {
      analytics.deprecatedTrackEvent(
        ELN_ANALYTICS_ACTIONS.FILLED_NEXT_AVAILABLE_POSITIONS_FROM_TRANSFER_TABLE_IN_NOTEBOOK,
        {entry_id: this.props.entryId}
      );
    }
  };

  _getEvaluatedEditableRowsAtIndices(indices: Array<number>): Array<Array<EvaluatedCell>> {
    const {computedTable} = this.props.itemProps;
    assert(!this._isPlaceholderTable(), 'Should only get evaluated rows when the transfer table has rows.');

    const evaluatedEditableRows = [];
    const numTransferredRows = this._getTransferredRows().length;
    for (const index of indices) {
      const tableIndex = index + numTransferredRows;
      if (tableIndex >= computedTable.length) {
        // This happens when rows have been added via the add modal but not yet rendered
        continue;
      }
      evaluatedEditableRows.push(computedTable[tableIndex]);
    }
    return evaluatedEditableRows;
  }

  _getEvaluatedEditableRows(): Array<Array<EvaluatedCell>> {
    return this._getEvaluatedEditableRowsAtIndices(_.range(this._getEditableRows().length));
  }

  _renderAddButton = (
    {isCollapsed}: TableHeaderControlsArgs,
    canAddSamples: boolean,
    addMenuItems: Array<TransferTableAddMenuItem>
  ): JSX.Element | null => {
    if (isCollapsed) {
      return null;
    }
    return (
      <Dropdown
        className="workflowTable-addDropdown"
        menu={
          <Menu width={Menu.WIDTHS.LARGE} height={Menu.HEIGHTS.MEDIUM}>
            {addMenuItems.map(({children, icon, onSelect}, index) => (
              <Option icon={icon} onSelect={onSelect} key={index}>
                {children}
              </Option>
            ))}
            {APIUtil.isBaseEntry(this.props.editorProps.fileId) && (
              <Option icon="upload" onSelect={this._handleInsertFromSpreadsheet}>
                Import from spreadsheet
              </Option>
            )}
          </Menu>
        }
      >
        <RichObjectTable.AddButton disabled={!canAddSamples || this.props.editorProps.locked} />
      </Dropdown>
    );
  };

  _hasValidationErrors(): boolean {
    if (this._isPlaceholderTable()) {
      return false;
    }

    return hasBlockingValidationErrors(this._getEvaluatedEditableRows());
  }

  /**
   * Return whether the transfer button is disabled and what its tooltip title is.
   */
  _getTransferButtonStatus(): ButtonStatus {
    if (this._isPlaceholderTable() || this._getEditableRows().length === 0) {
      return {
        tooltipTitle: 'Add samples to transfer',
        disabled: true,
      };
    } else if (this._hasValidationErrors()) {
      return {
        tooltipTitle: 'Fix validation errors to transfer',
        disabled: true,
      };
    } else {
      return {
        tooltipTitle: 'Transfer',
        disabled: false,
      };
    }
  }

  _renderTransferButton(): JSX.Element {
    const {tooltipTitle, disabled} = this._getTransferButtonStatus();
    return (
      <Tooltip title={tooltipTitle}>
        <span>
          <Button
            buttonStyle={ButtonStyle.SECONDARY}
            className="u-mini-spacing-left"
            disabled={this.props.editorProps.locked || disabled}
            icon="transfer"
            onClick={this._handleTransfer}
            size={ButtonSize.MINI}
            text="Transfer"
            dataTestAttrs={{component: 'LegacyTransferTable', element: 'button', key: 'transfer'}}
          />
        </span>
      </Tooltip>
    );
  }

  /**
   * Return whether the fill grid button is disabled and what its tooltip title is.
   */
  _getFillGridButtonStatus(): ButtonStatus {
    if (
      this._isPlaceholderTable() ||
      this._getParentStorableIds(this._getEvaluatedEditableRows()).length === 0
    ) {
      return {
        tooltipTitle: 'No boxes or plates to fill',
        disabled: true,
      };
    } else {
      return {
        tooltipTitle: 'Fill next available positions',
        disabled: false,
      };
    }
  }

  _renderFillGridButton(): JSX.Element {
    const {tooltipTitle, disabled} = this._getFillGridButtonStatus();
    return (
      <Tooltip title={tooltipTitle}>
        <span>
          <Dropdown
            menu={
              <Menu width={Menu.WIDTHS.SMALL} height={Menu.HEIGHTS.MEDIUM}>
                <Option
                  icon="fill-across-rows"
                  iconClassName="u-faded"
                  value={OrderingStrategy.ROW_FIRST}
                  onSelect={this._handleFillNextAvailablePositions}
                >
                  Across rows
                </Option>
                <Option
                  icon="fill-down-columns"
                  iconClassName="u-faded"
                  value={OrderingStrategy.COLUMN_FIRST}
                  onSelect={this._handleFillNextAvailablePositions}
                >
                  Down columns
                </Option>
              </Menu>
            }
          >
            <Button
              aria-label={tooltipTitle}
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              className="u-mini-spacing-left"
              disabled={this.props.editorProps.locked || disabled}
              icon="fill-grid"
              isDropdown={true}
              size={ButtonSize.MINI}
            />
          </Dropdown>
        </span>
      </Tooltip>
    );
  }

  _renderExportToCSVButton(): JSX.Element {
    return (
      <SimpleDownloadAsCSVButton
        onDownloadAsCSV={() => {
          this._handleDownloadAsCSV();
        }}
      />
    );
  }

  _renderCreateRequestButton(): JSX.Element {
    // This function is all or nothing; it will return null if the data are still loading (i.e.,
    // if the required values are not present in the "*byId" mappings)
    const samples = this.getRequestableSamples(
      this.props.itemProps.table,
      this.props.rows,
      this.props.fileSummariesById,
      this.props.batchesById
    );

    return (
      <CreateRequestFromSamplesButton
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        className="u-mini-spacing-left"
        isLoadingSamples={samples == null}
        samples={samples || []}
        size={ButtonSize.MINI}
      />
    );
  }

  _renderHeaderControls({isCollapsed, multiSelectionBox}: TableHeaderControlsArgs): JSX.Element {
    if (isCollapsed) {
      return this._renderExportToCSVButton();
    }
    const doRefresh = async (): Promise<void> => {
      await handleRefreshTableColumns({
        entryOrTemplateId: this.props.editorProps.fileId,
        provenanceUuid: this.props.provenanceUuid,
        onUpdateTable: ({
          columnUpdates,
        }: {
          columnUpdates: {[columnId: number]: SchemaFieldColumnUpdate | {}};
        }) => {
          const columnConstraints = mergeColumnUpdatesIntoColumnConstraints(
            this.props.itemProps.table,
            columnUpdates,
            this.props.editorProps.getIdFromCid
          );
          const newTable = {...this.props.itemProps.table, columnConstraints};
          this.props.updateHandler(this.props.index, {table: newTable}, undefined, {
            [this.props.tableName]: {type: 'eval-table'},
          });
        },
      });
    };

    return (
      <span className="u-vertical-center-items">
        <PrintBarcodesButton
          table={this.props.itemProps.table}
          multiSelectionBox={multiSelectionBox}
          columns={this.props.structuredTableColumns}
          computedTable={this.props.itemProps.computedTable}
        />
        <TableHeaderPipeDivider />
        <RefreshStructuredTableButton
          entryOrTemplateId={this.props.editorProps.fileId}
          doRefresh={doRefresh}
          structuredTableKind="OLD Transfer table"
          disabled={this.props.editorProps.locked}
        />
        {this.props.parentStorableIsGrid && this._renderFillGridButton()}
        {BenchlingConfig.ENABLE_REQUESTS && this._renderCreateRequestButton()}
        {this._renderExportToCSVButton()}
        {this._renderTransferButton()}
      </span>
    );
  }

  _getLinkContext():
    | typeof TransferTableStageRunSampleLinkContext
    | typeof TransferTableStandaloneLinkContext {
    return this.props.stageRuns && this.props.stageRuns.length > 0
      ? TransferTableStageRunSampleLinkContext
      : TransferTableStandaloneLinkContext;
  }

  override render(): JSX.Element {
    const allEditorProps = NoteEditorTableUtil.tableEditablePropsFromEditor(
      this.props.editor,
      this.props.editorProps
    );
    Object.assign(allEditorProps, {
      deleteHandler: () => {
        this.props.editor.itemDeleteHandler(this.props.index);
      },
    });

    const LinkContext = this._getLinkContext();
    return (
      <LinkContext
        stageRuns={this.props.stageRuns}
        registrationTables={this.props.registrationTables}
        onAddSampleRows={this._handleAddSamples}
        entryId={this.props.entryId}
        note={this.props.editor.props.note}
      >
        {({
          canAddSamples,
          addMenuItems,
        }: {
          canAddSamples: boolean;
          addMenuItems: Array<TransferTableAddMenuItem>;
        }) => (
          <TableEditable
            {...allEditorProps}
            {...this.props.itemProps}
            canAddRemoveColumns={this._getTransferredRows().length === 0}
            cid={this.props.cid}
            headerControls={(args) => this._renderHeaderControls(args)}
            leftHeaderControls={(args) => this._renderAddButton(args, canAddSamples, addMenuItems)}
            collapseId={createCollapseId(this.props.editorProps.fileId, this.props.itemProps.table.name)}
            disableFormulas={false}
            disableLegacyHeader={true}
            icon={this.props.icon}
            indentation={this.props.indentation}
            index={this.props.index}
            insertableColumnInfos={(() => {
              const defaultColumns = this._getDefaultStructuredTableColumns().map(
                (structuredTableColumn) => ({
                  structuredTableColumn,
                  columnDefinition: this._structuredTableColumnToColumnDefinition(structuredTableColumn),
                })
              );
              return toInsertableColumnInfos(defaultColumns, this.props.structuredTableColumns);
            })()}
            isCollapsible={true}
            isCompactBottomSpacing={false}
            isLeftAlignColumnHeader={true}
            disableMergingCells={true}
            mouseDownHandler={this.props.mouseDownHandler}
            mouseOverHandler={this.props.mouseOverHandler}
            selectorsEnabled={false}
            selectorsLink={TransferTable.selectorsLink}
            openManageSelectorsModal={noop}
            showFormulaBox={true}
            tableLabel={this.props.tableTypeLabel}
            table={this.props.itemProps.table}
            updateHandler={this.props.updateHandler}
            dataTestAttrs={{component: 'LegacyTransferTable', element: 'table'}}
            {...forwardSearchResultProps(this.props)}
          />
        )}
      </LinkContext>
    );
  }
}

/**
 * @deprecated This table is no longer supported, please do not further proliferate its use.
 */
export default TransferTableConnector.connect(TransferTable);
